import React, { createContext, useState, useContext, useEffect } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";


// Create context
const OfferLettersContext = createContext();

// Provider component
export const OfferLettersProvider = ({ children }) => {
  const [offerLetters, setOfferLetters] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE = "https://hrmsnew.prepstripe.com/api";
  const token = getToken();

  // Fetch offer letters with pagination
  const fetchOfferLetters = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE}/offer-letters?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = response.data.data;
      setOfferLetters(responseData.data || []);
      setPagination({
        currentPage: responseData.current_page,
        totalPages: responseData.last_page,
        totalItems: responseData.total,
        perPage: responseData.per_page,
      });
    } catch (err) {
      setError("Failed to fetch offer letters.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Add a new offer letter
  const createOfferLetter = async (formData) => {
    try {
      const response = await axios.post(`${API_BASE}/offer-letters`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // alert(response.data.message);
      fetchOfferLetters(pagination.currentPage); // Refresh the list for the current page
    } catch (err) {
      throw new Error("Failed to create offer letter.");
    }
  };

  // Update an offer letter
  const updateOfferLetter = async (id, offerLetterData) => {
    try {
      const response = await axios.put(`${API_BASE}/offer-letters/${id}`, offerLetterData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // alert(response.data.message);
      fetchOfferLetters(pagination.currentPage); // Refresh the current page
    } catch (err) {
      throw new Error(err.response?.data?.message || "Failed to update offer letter.");
    }
  };

  // Delete an offer letter
  const deleteOfferLetter = async (id) => {
    try {
      await axios.delete(`${API_BASE}/offer-letters/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // alert("Offer letter deleted.");
      fetchOfferLetters(pagination.currentPage); // Refresh the list for the current page
    } catch (err) {
      throw new Error("Failed to delete offer letter.");
    }
  };

  useEffect(() => {
    fetchOfferLetters(); // Fetch the first page on initial load
  }, []);

  return (
    <OfferLettersContext.Provider
      value={{
        offerLetters,
        pagination,
        loading,
        error,
        fetchOfferLetters,
        createOfferLetter,
        updateOfferLetter,
        deleteOfferLetter,
      }}>
      {children}
    </OfferLettersContext.Provider>
  );
};

// Custom hook for using context
export const useOfferLetters = () => {
  return useContext(OfferLettersContext);
};
